/**
 * Buy Widget Tabs — controller.
 *
 * Drives the tab bar + failure-fallback in the buy-widget-tabs block. The PRIMARY
 * provider loads itself (Transak via application.js's global observer on
 * [data-transak-widget]; Simplex primary via its eager inline embed). This
 * controller handles the SECONDARY provider, lazily, on first activation
 * (tab click OR fallback):
 *   - Transak → wake the dormant [data-transak-widget-deferred] embed + new TransakWidget.
 *   - Simplex → read the deferred config blob + run SimplexLoader.
 *
 * Failure fallback (only when a secondary exists, i.e. DUAL / AB tab layouts):
 * when the ACTIVE provider signals *_widget_load_failed or *_widget_stuck, a
 * "try another provider" CTA is surfaced; clicking it swaps to the other,
 * genuinely-supported provider (providers[1]) via the same activation path. Since
 * single-provider countries have no secondary, the fallback can NEVER surface
 * Transak in MiCA / XX / T1 (those resolve to a single Simplex provider).
 *
 * @see template-parts/blocks/buy-widget-tabs/buy-widget-tabs.php
 * @see src/js/components/simplex-loader.js
 * @see src/js/components/transak.js
 */

import TransakWidget from './transak';
import SimplexLoader from './simplex-loader';

/** dataLayer events that mean "this provider failed to come up". */
const FAILURE_EVENTS = {
    transak: ['transak_widget_load_failed', 'transak_widget_stuck'],
    simplex: ['simplex_load_failed', 'simplex_widget_stuck', 'simplex_lazy_init_failed'],
};

export default class BuyWidgetTabs {

    /**
     * @param {HTMLElement} el - the [data-buy-widget-tabs] root.
     */
    constructor(el) {
        this.el     = el;
        this.nav    = el.querySelector('[data-tabs-nav]');
        this.tabs   = Array.from(el.querySelectorAll('[data-tab]'));
        this.panels = Array.from(el.querySelectorAll('[data-tab-panel]'));

        // No tab bar → single provider, nothing to drive (no tabs, no fallback).
        if (!this.nav || this.tabs.length < 2) return;

        this.primary   = el.dataset.primary || '';
        this.secondary = el.dataset.secondary || '';
        this.active    = this.primary;              // primary shown first
        this._loaded   = new Set([this.primary]);   // primary loads via its own eager path

        this.fallback     = el.querySelector('[data-buy-fallback]');
        this.fallbackCta  = el.querySelector('[data-buy-fallback-cta]');
        this._fallbackShown = false;

        this.tabs.forEach((tab) => {
            tab.addEventListener('click', () => this._switchTo(tab.dataset.tab));
        });

        if (this.fallbackCta) {
            this.fallbackCta.addEventListener('click', () => this._onFallbackClick());
        }

        this._watchFailures();
    }

    /**
     * @param {string} provider
     * @param {boolean} [viaFallback] - when true, suppress the tab_switched event
     *   (the fallback path emits its own buy_widget_fallback_clicked instead, so
     *   fallback CTR and tab-switch-rate stay separate metrics).
     */
    _switchTo(provider, viaFallback = false) {
        if (!provider || provider === this.active) return;

        const from = this.active;
        this.active = provider;

        this.panels.forEach((panel) => {
            panel.hidden = panel.dataset.tabPanel !== provider;
        });

        this.tabs.forEach((tab) => {
            const selected = tab.dataset.tab === provider;
            tab.setAttribute('aria-selected', selected ? 'true' : 'false');
            // Underline-indicator style (mirrors tabs-cards): active = purple
            // underline, inactive = transparent. Text colour is fixed per theme in
            // the template, so nothing else toggles — works on light or dark.
            tab.classList.toggle('border-purple-700', selected);
            tab.classList.toggle('border-transparent', !selected);
        });

        // Switching away from the failed provider — retract the fallback CTA.
        this._hideFallback();

        if (!this._loaded.has(provider)) {
            this._loaded.add(provider);
            this._activate(provider);
        }

        if (!viaFallback) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'buy_widget_tab_switched', from, to: provider });
        }
    }

    /** Bring a deferred secondary provider to life. */
    _activate(provider) {
        const panel = this.panels.find((p) => p.dataset.tabPanel === provider);
        if (!panel) return;

        if (provider === 'transak') {
            const dormant = panel.querySelector('[data-transak-widget-deferred]');
            if (!dormant) return;
            dormant.setAttribute('data-transak-widget', '');
            dormant.removeAttribute('data-transak-widget-deferred');
            try { new TransakWidget(dormant); } catch (e) { /* TransakWidget logs its own errors */ }
            return;
        }

        if (provider === 'simplex') {
            const configEl = panel.querySelector('[data-simplex-config]');
            if (!configEl) return;
            let config;
            try { config = JSON.parse(configEl.textContent); } catch (e) { return; }
            new SimplexLoader(config).load();
        }
    }

    // -------------------------------------------------------------------------
    // Failure fallback
    // -------------------------------------------------------------------------

    /** Tap dataLayer so we see the widgets' failure signals without disturbing GTM. */
    _watchFailures() {
        window.dataLayer = window.dataLayer || [];
        const original = window.dataLayer.push.bind(window.dataLayer);
        window.dataLayer.push = (...events) => {
            events.forEach((e) => { if (e && e.event) this._onDataLayer(e.event); });
            return original(...events);
        };
    }

    _onDataLayer(eventName) {
        if (this._fallbackShown) return;
        const events = FAILURE_EVENTS[this.active];
        if (!events || !events.includes(eventName)) return;

        // The timeout-based "stuck" signals are ambiguous — a slow-but-successful
        // load can trip them. Suppress the fallback for those if the widget has
        // actually rendered. Explicit failures (*_load_failed / *_lazy_init_failed)
        // are definitive and always surface the fallback.
        const isStuckSignal = eventName.endsWith('_widget_stuck');
        if (isStuckSignal && this._activeWidgetRendered()) return;

        this._showFallback();
    }

    /** True when the active provider's panel already contains a rendered widget iframe. */
    _activeWidgetRendered() {
        const panel = this.panels.find((p) => p.dataset.tabPanel === this.active);
        return !!panel && !!panel.querySelector('iframe');
    }

    _showFallback() {
        if (!this.fallback || this._fallbackShown) return;
        this._fallbackShown = true;
        this.fallback.classList.remove('hidden');

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'buy_widget_fallback_shown',
            from:  this.active,
            to:    this._other(),
        });
    }

    _hideFallback() {
        if (!this.fallback) return;
        this.fallback.classList.add('hidden');
        this._fallbackShown = false;
    }

    _onFallbackClick() {
        const from = this.active;
        const to   = this._other();

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'buy_widget_fallback_clicked', from, to });

        this._switchTo(to, true); // viaFallback → no tab_switched double-count
    }

    /** The other provider relative to the active one (always the supported alternate). */
    _other() {
        return this.active === this.primary ? this.secondary : this.primary;
    }
}
